import React, { Fragment, Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { NavLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactTooltip from "react-tooltip";
import {
  faEnvelope,
  faUserCircle,
  faInfoCircle,
  faPhone,
  faGlobe,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import { faLinkedin } from "@fortawesome/free-brands-svg-icons";
import HiringHubScore from "./HiringHubScore";
import userType from "./types/userType";
import UserEdit from "./EditForms/UserEdit";
/* eslint-disable-next-line import/no-named-as-default */
import AvatarEdit from "./EditForms/AvatarEdit";
import EditButton from "../shared/button/EditButton";
import WidgetLoading from "../shared/loaders/WidgetLoading";
import {
  Wrapper,
  Avatar,
  YourAgentBadge,
  AvatarImage,
  AvatarPlaceholder,
  Info,
  Line,
  LineLink,
  NameText,
  VerifiedBadgeWrapper,
  VerifiedBadge,
  Contact,
  Score,
  UserScore,
  StarRating,
  ReviewsCount,
  FillRating,
  Stars,
  EmptyRating,
  NewBadge,
  MobileMemberSince,
  Tooltip,
} from "./styles/user-styles";

export class User extends Component {
  state = {
    showContactActive: false,
    editUserInfoActive: false,
    editAvatarActive: false,
  };

  toggleContactInfo = () => {
    this.setState(({ showContactActive }) => ({
      showContactActive: !showContactActive,
    }));
  };

  toggleEditUserInfo = () => {
    this.setState(({ editUserInfoActive }) => ({
      editUserInfoActive: !editUserInfoActive,
    }));
  };

  toggleEditAvatar = () => {
    this.setState(({ editAvatarActive }) => ({
      editAvatarActive: !editAvatarActive,
    }));
  };

  handleNameLength = (name) => {
    if (name.length > 20) {
      return `${name.slice(0, 20)}...`;
    }
    return name;
  };

  render() {
    const { showContactActive, editUserInfoActive, editAvatarActive } =
      this.state;
    const {
      user,
      currentUser,
      companyPerformance,
      userFormLoading,
      employerAndApplicantReviewsTotal,
      numberOfPlacements,
      hhScore,
    } = this.props;

    const ratingOutOfFive = (user.score.starRatingPercentage / 100.0) * 5;
    const roundRatingToNearestDecimal = (
      Math.round(ratingOutOfFive * 10) / 10
    ).toFixed(1);

    return (
      <Wrapper className="animated delay-2 fadeIn">
        {editAvatarActive && (
          <AvatarEdit toggleEditAvatar={this.toggleEditAvatar} />
        )}
        {userFormLoading && <WidgetLoading />}
        {currentUser.isRecruiter && (
          <EditButton
            onClickAction={this.toggleEditUserInfo}
            top={8}
            right={10}
          />
        )}
        <Avatar>
          {currentUser.isRecruiter && (
            <EditButton
              onClickAction={this.toggleEditAvatar}
              top={8}
              right={8}
            />
          )}
          {user.avatar ? (
            <AvatarImage src={user.avatar} alt="Avatar" />
          ) : (
            <AvatarPlaceholder>
              <FontAwesomeIcon icon={faUserCircle} />
            </AvatarPlaceholder>
          )}
          <YourAgentBadge>your agent</YourAgentBadge>
        </Avatar>
        <Info>
          {editUserInfoActive && (
            <UserEdit toggleEditUserInfo={this.toggleEditUserInfo} />
          )}
          {showContactActive ? (
            <Fragment>
              <Line>
                <LineLink href={`mailto:${user.email}`} title="Email">
                  <FontAwesomeIcon icon={faEnvelope} />
                  {user.email}
                </LineLink>
              </Line>
              {user.phone && (
                <Line>
                  <LineLink href={`tel:${user.phone}`} title="Phone">
                    <FontAwesomeIcon icon={faPhone} />
                    {user.phone}
                  </LineLink>
                </Line>
              )}
              {user.linkedin && (
                <Line>
                  <LineLink
                    href={user.linkedin}
                    target="_blank"
                    title="LinkedIn"
                  >
                    <FontAwesomeIcon icon={faLinkedin} />
                    LinkedIn
                  </LineLink>
                </Line>
              )}
              {user.website && (
                <Line>
                  <LineLink href={user.website} target="_blank" title="Website">
                    <FontAwesomeIcon icon={faGlobe} />
                    Website Address
                  </LineLink>
                </Line>
              )}
            </Fragment>
          ) : (
            <Fragment>
              <Line>
                <NameText data-test-id="user-name">
                  {this.handleNameLength(`${user.firstName} ${user.lastName}`)}
                  <VerifiedBadgeWrapper
                    data-tip="Verified"
                    data-for="agency-profile-tooltip"
                    data-test-id="verified-badge-wrapper"
                  >
                    {numberOfPlacements >= 3 && (
                      <VerifiedBadge>
                        <FontAwesomeIcon icon={faCheck} />
                      </VerifiedBadge>
                    )}
                  </VerifiedBadgeWrapper>
                </NameText>
              </Line>
              <Line data-test-id="company-name" bold>
                {user.companyName}
              </Line>
              <Line data-test-id="user-location">{user.location}</Line>
              <Line data-test-id="user-experience">
                {user.yearsExperience} years experience
              </Line>
            </Fragment>
          )}
        </Info>
        <Contact
          data-test-id="show-contact-link"
          onClick={this.toggleContactInfo}
          new={user.new}
        >
          {showContactActive ? "Hide Contact" : "Show Contact"}
        </Contact>
        {!user.new || (user.new && user.score.starRatingPercentage > 0) ? (
          <Score>
            <UserScore>
              <StarRating>
                <FillRating
                  data-test-id="fill-rating"
                  style={{ width: `${user.score.starRatingPercentage}%` }}
                  aria-label={`${roundRatingToNearestDecimal} out of 5 stars`}
                >
                  <Stars>★★★★★</Stars>
                </FillRating>
                <EmptyRating data-test-id="empty-rating">
                  <Stars>★★★★★</Stars>
                </EmptyRating>
              </StarRating>
              <ReviewsCount data-test-id="review-count">
                <NavLink
                  to="/reviews"
                  onClick={() => {
                    const element = document.getElementById("tab-nav");
                    element.scrollIntoView({
                      behavior: "smooth",
                    });
                  }}
                >
                  {employerAndApplicantReviewsTotal} reviews
                </NavLink>
                <Tooltip
                  data-tip="This star rating is calculated by taking the average score from all employer and candidate reviews"
                  data-for="agency-reviews-tooltip"
                >
                  <FontAwesomeIcon icon={faInfoCircle} />
                </Tooltip>
                <ReactTooltip
                  place="bottom"
                  type="dark"
                  effect="solid"
                  aria-haspopup="true"
                  id="agency-reviews-tooltip"
                />
              </ReviewsCount>
            </UserScore>
            {!user.new && (
              <HiringHubScore
                hhScore={hhScore}
                isRecruiter={currentUser.isRecruiter}
              />
            )}
          </Score>
        ) : null}
        {user.new && <NewBadge>new!</NewBadge>}
        <MobileMemberSince>
          Member since {companyPerformance.memberSince}
        </MobileMemberSince>
      </Wrapper>
    );
  }
}

User.propTypes = {
  user: userType.isRequired,
  currentUser: PropTypes.shape({
    isRecruiter: PropTypes.bool.isRequired,
  }).isRequired,
  companyPerformance: PropTypes.shape({
    memberSince: PropTypes.string,
  }).isRequired,
  userFormLoading: PropTypes.bool.isRequired,
  employerAndApplicantReviewsTotal: PropTypes.number.isRequired,
  numberOfPlacements: PropTypes.number,
  hhScore: PropTypes.number.isRequired,
};

User.defaultProps = {
  numberOfPlacements: PropTypes.isNull,
};

const mapStateToProps = (state) => ({
  user: state.agencyProfile.profileData.user,
  currentUser: state.agencyProfile.profileData.currentUser,
  hhScore: state.agencyProfile.profileData.hhScore,
  companyPerformance: state.agencyProfile.profileData.companyPerformance,
  userFormLoading: state.agencyProfile.userFormLoading,
  employerAndApplicantReviewsTotal:
    state.agencyProfile.profileData.employerAndApplicantReviewsTotal,
  numberOfPlacements:
    state.agencyProfile.profileData.companyPlacementHistory.length,
});

export default connect(mapStateToProps, {})(User);
